import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import type { Friend, FriendAvatar as FriendAvatarData } from "@boo/shared";
import { Button } from "@/components/ui/button";
import FriendAvatar from "@/components/FriendAvatar";
import { AVATAR_ACCEPT, cropAvatar } from "../lib/cropAvatar";
import { QUICK_EMOJIS, isSingleEmoji } from "../lib/emojis";
import { INPUT_CLASS, type AvatarDraft } from "../types/friends.types";

interface AvatarEditorProps {
  friend: Friend;
  name: string;
  draft: AvatarDraft;
  onChange: (draft: AvatarDraft) => void;
}

/** What the avatar will look like once saved. */
function previewOf(
  friend: Friend,
  draft: AvatarDraft,
): { avatar: FriendAvatarData | null; photoSrc?: string } {
  switch (draft.kind) {
    case "emoji":
      return { avatar: { kind: "emoji", emoji: draft.emoji } };
    case "photo":
      return {
        avatar: { kind: "image", version: 0 },
        photoSrc: draft.previewUrl,
      };
    case "none":
      return { avatar: null };
    default:
      return { avatar: friend.avatar };
  }
}

export default function AvatarEditor({
  friend,
  name,
  draft,
  onChange,
}: AvatarEditorProps) {
  const [typed, setTyped] = useState("");
  const [cropping, setCropping] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const previewUrl = draft.kind === "photo" ? draft.previewUrl : null;

  // Staged photos are blob URLs; free them when replaced or the modal closes.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const { avatar, photoSrc } = previewOf(friend, draft);
  const shown = draft.kind === "unchanged" ? friend.avatar : avatar;
  const hasAvatar = shown !== null;

  async function handleFile(file: File | undefined) {
    if (fileInput.current) fileInput.current.value = "";
    if (!file) return;
    if (!AVATAR_ACCEPT.split(",").includes(file.type)) {
      toast.error("Use a JPEG, PNG or WebP image");
      return;
    }
    setCropping(true);
    try {
      const blob = await cropAvatar(file);
      onChange({ kind: "photo", blob, previewUrl: URL.createObjectURL(blob) });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not process the image",
      );
    } finally {
      setCropping(false);
    }
  }

  function handleTyped() {
    const emoji = typed.trim();
    if (!emoji) return;
    if (!isSingleEmoji(emoji)) {
      toast.error("Avatar must be a single emoji");
      return;
    }
    onChange({ kind: "emoji", emoji });
    setTyped("");
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <FriendAvatar
          name={name || friend.name}
          avatar={avatar}
          friendId={friend.id}
          photoSrc={photoSrc}
          size={56}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            loading={cropping}
            onClick={() => fileInput.current?.click()}
          >
            {!cropping && <Camera className="mr-1 size-4" />}
            Upload photo
          </Button>
          {hasAvatar && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ kind: "none" })}
            >
              <Trash2 className="mr-1 size-4" />
              Remove
            </Button>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept={AVATAR_ACCEPT}
          className="hidden"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
      </div>

      <div className="grid grid-cols-8 gap-1">
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onChange({ kind: "emoji", emoji })}
            aria-label={`Use ${emoji}`}
            className={`flex aspect-square items-center justify-center rounded-md text-xl hover:bg-accent active:bg-accent ${
              shown?.kind === "emoji" && shown.emoji === emoji
                ? "bg-accent ring-1 ring-primary"
                : ""
            }`}
          >
            {emoji}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            handleTyped();
          }}
          placeholder="Type or paste any emoji"
          aria-label="Type or paste any emoji"
          className={`${INPUT_CLASS} min-w-0 flex-1 py-2 text-base sm:text-sm`}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!typed.trim()}
          onClick={handleTyped}
        >
          Use
        </Button>
      </div>
    </div>
  );
}
